import React, { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Button } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ebenchLogo from "../../../images/ebench_logo.png";
import "react-toastify/dist/ReactToastify.css";
import "./OtpVerify.scss";
import { useResendOtp } from "../../../Hooks/useResendOtp";
import { verifyCandidateOtp } from "../../../Services/authService";
import setAuthorizationToken from "../../../utils/setAutorization";

const OtpVerify = () => {
  const navigate = useNavigate();
  const { state: LoginData } = useLocation();
  const { mutate: resendOtp } = useResendOtp();

  const [isLoading, setIsLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(60));
  const [isResendVisible, setIsResendVisible] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);

  const inputRefs = useRef([]);

  const email = LoginData?.email;
  const userType = LoginData?.userType;
  const otp = otpValues.join("");
  const data = { otp, email };

  const handleInputChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return; 
    const newOtp = [...otpValues];
    newOtp[index] = value;
    setOtpValues(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    } else if (!value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  useEffect(() => {
    if (secondsLeft === 0) {
      setIsResendVisible(true);
      return;
    }
    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otp) {
      toast.error("Please enter your OTP!", { toastId: "otpError" });
      return;
    }

    try {
      await verifyCandidateOtp(data);
      toast.success("Verification successful!");

      if (LoginData?.logIn) {
        localStorage.setItem("adminid", LoginData?.adminId);
        setAuthorizationToken(LoginData?.token);

        setTimeout(() => {
          switch (LoginData?.userType) {
            case "VENDOR":
              navigate("/vendor_dashboard");
              break;
            case "ADMIN":
              navigate("/admin");
              break;
            default:
              navigate("/candidate_dashboard");
          }
        }, 1000);
      } else {
        navigate("/login");
      }
    } catch (error) {
      console.error("Invalid OTP or verification failed.");
    }
  };

  const handleResendOtp = () => {
    setIsLoading(true);
    resendOtp(
      { email, userType },
      {
        onSuccess: () => {
          setIsLoading(false);
          setSecondsLeft(60);
          setIsResendVisible(false);
        },
        onError: () => {
          setIsLoading(false);
          toast.error("Failed to resend OTP. Try again.");
        },
      }
    );
  };

  return (
    <>
      <ToastContainer />
      {/* ===== HEADER ===== */}
      <header className="d-flex justify-content-between align-items-center px-5 py-3 shadow-sm bg-white fixed-top">
        <div className="landing_logo">
          <Link to="/">
            <img
              src={ebenchLogo}
              alt="Ebench Logo"
              className="responsive-logo-of-without-login"
            />
          </Link>
        </div>
        <div className="landing_menu_items d-flex align-items-center gap-3">
          <Link to="/login">
            <button
              className="btn btn-outline-primary-custom"
              style={{ padding: "6px 20px", fontWeight: 600 }}
            >
              Login
            </button>
          </Link>
        </div>
      </header>

      {/* ===== OTP FORM ===== */}
      <div className="otp-page d-flex align-items-center justify-content-center">
        <div className="otp-card text-center shadow">
          <h4 className="mb-4">Enter Verification OTP</h4>
          <form onSubmit={handleSubmit}>
            <p className="mb-3">Type your 6-digit security code</p>
            <div className="d-flex justify-content-center gap-2 mb-3">
              {otpValues.map((value, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  maxLength={1}
                  className="otp-box text-center"
                  value={value}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                />
              ))}
            </div>

            <Button
              type="submit"
              variant="contained"
              style={{
                padding: "10px 27px",
                background: "#286a94",
                marginTop: "12px",
              }}
            >
              Submit
            </Button>

            <div className="mt-4 resend-section text-center">
              {isResendVisible ? (
                <div className="resend-wrapper">
                  <p className="resend-text mb-2">Didn’t get the code?</p>
                  <Button
                    onClick={handleResendOtp}
                    disabled={isLoading}
                    variant="contained"
                    sx={{
                      background: "linear-gradient(135deg, #286a94, #1e4e70)",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "14px",
                      textTransform: "none",
                      borderRadius: "8px",
                      px: 3,
                      py: 1,
                      boxShadow: "0 4px 12px rgba(40,106,148,0.25)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "linear-gradient(135deg, #307aa6, #245b80)",
                        boxShadow: "0 6px 18px rgba(40,106,148,0.35)",
                        transform: "translateY(-2px)",
                      },
                      "&:disabled": {
                        background: "rgba(40,106,148,0.4)",
                        boxShadow: "none",
                        transform: "none",
                      },
                    }}
                  >
                    {isLoading ? "Resending..." : "Resend OTP"}
                  </Button>
                </div>
              ) : (
                <p className="countdown-text mt-2">
                  Resend OTP in{" "}
                  <strong style={{ color: "#286a94" }}>{secondsLeft}s</strong>
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default OtpVerify;
